import React, { useState } from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  CreditCardIcon,
  TruckIcon,
  HomeIcon,
  UserIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { baseURL } from "../../../lib/axios";

const OrderDetailModal = ({
  orderData,
  isShopOwner = false,
  isAdmin = true,
  onClose,
  onUpdateStatus,
  onCancelOrder,
  onValidateOrder,
  onShipOrder,
  onMarkDelivered,
  onRefundOrder,
  onConfirmPayment,
}) => {
  const [transactionId, setTransactionId] = useState("");
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);

  // Adapter pour géreré différentes structures de données selon le rôle
  const order = isShopOwner ? orderData.order : orderData;
  const items = isShopOwner
    ? [orderData.item] // Pour propriétaire de boutique, item spécifique
    : order.order_items || []; // Pour utilisateur ou admin, tous les items

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "En attente",
      processing: "En traitement",
      shipped: "Expédiée",
      delivered: "Livrée",
      cancelled: "Annulée",
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: "En attente",
      paid: "Payée",
      failed: "Échouée",
    };
    return labels[status] || status;
  };

  const getPaymentStatusBadgeClass = (status) => {
    const classes = {
      pending: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const handleConfirmPayment = () => {
    if (transactionId.trim()) {
      onConfirmPayment(transactionId);
      setShowPaymentConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">
            Détails de la commande #{order.id}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Informations Client */}
          {order.user && isAdmin && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <UserIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Informations Client
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-600 text-sm">Nom complet:</span>
                  <p className="font-medium">
                    {order.user.firstname} {order.user.lastname}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Téléphone:</span>
                  <p className="font-medium">{order.user.phone_number}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Email:</span>
                  <p className="font-medium">{order.user.email}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">
                    Adresse de livraison:
                  </span>
                  <p className="font-medium">
                    {order.adresse || "Non renseignée"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Informations générales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                Commande
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-gray-600 text-sm">Date:</span>
                  <p className="font-medium text-sm">
                    {formatDate(order.created_at)}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Statut:</span>
                  <p>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 mt-1 rounded-full text-xs font-medium ${getStatusBadgeClass(order.status)}`}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                Paiement
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-gray-600 text-sm">Méthode:</span>
                  <p className="font-medium capitalize">
                    {order.payment_method}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Statut:</span>
                  <p>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 mt-1 rounded-full text-xs font-medium ${getPaymentStatusBadgeClass(order.payment_status)}`}
                    >
                      {getPaymentStatusLabel(order.payment_status)}
                    </span>
                  </p>
                </div>
                {order.payment?.transaction_id && (
                  <div>
                    <span className="text-gray-600 text-sm">Transaction:</span>
                    <p className="font-medium text-xs">
                      {order.payment.transaction_id}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-md font-medium text-gray-900 mb-3">Résumé</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-gray-600 text-sm">Boutiques:</span>
                  <p className="font-medium">{order.total_shops || 1}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Articles:</span>
                  <p className="font-medium">{items.length}</p>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Total:</span>
                  <p className="font-bold text-blue-600">
                    {formatPrice(order.total_amount)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Résumé par Boutique */}
          {order.shops_summary && order.shops_summary.length > 0 && (
            <div>
              <div className="flex items-center mb-3">
                <BuildingStorefrontIcon className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Détails par Boutique
                </h3>
              </div>
              <div className="space-y-4">
                {order.shops_summary.map((shop, idx) => (
                  <div
                    key={idx}
                    className="border border-purple-200 rounded-lg p-4 bg-purple-50"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {shop.shop_logo && (
                          <img
                            src={`${baseURL}/storage/${shop.shop_logo}`}
                            alt={shop.shop_name}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {shop.shop_name}
                            {shop.shop_phone && (
                              <span className="ml-2 text-sm text-blue-600 font-normal">
                                ({shop.shop_phone})
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Vendeur: {shop.shop_owner}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {shop.items_count} article(s)
                        </p>
                        <p className="font-bold text-purple-700">
                          {formatPrice(shop.subtotal)}
                        </p>
                      </div>
                    </div>

                    {/* Articles de cette boutique */}
                    <div className="mt-3 space-y-2 border-t border-purple-200 pt-3">
                      {shop.items &&
                        shop.items.map((item, itemIdx) => (
                          <div
                            key={itemIdx}
                            className="flex justify-between items-center bg-white rounded p-2"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">
                                {item.product_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.quantity} x {formatPrice(item.unit_price)}
                              </p>
                            </div>
                            <p className="font-medium">
                              {formatPrice(item.subtotal)}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liste complète des produits */}
          <div>
            <div className="flex items-center mb-3">
              <ShoppingBagIcon className="h-6 w-6 text-green-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Tous les Produits
              </h3>
            </div>
            <div className="border rounded-md divide-y divide-gray-200">
              {items.length > 0 ? (
                items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center p-4 hover:bg-gray-50"
                  >
                    <div className="flex-shrink-0 h-20 w-20 mr-4">
                      {item.product?.images && (
                        <img
                          src={`${baseURL}/storage/${JSON.parse(item.product.images)[0]}`}
                          alt={item.product.name}
                          className="h-full w-full object-cover rounded"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {item.product?.name || "Produit non disponible"}
                      </p>
                      {item.product?.category && (
                        <p className="text-xs text-gray-500 mt-1">
                          Catégorie: {item.product.category.name}
                        </p>
                      )}
                      {item.product?.shop && (
                        <p className="text-xs text-blue-600 mt-1">
                          Boutique: {item.product.shop.name}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        {item.quantity} x {formatPrice(item.price)}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right ml-4">
                      <p className="text-sm font-bold text-gray-900">
                        {formatPrice(item.subtotal)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  Aucun produit trouvé pour cette commande
                </div>
              )}
            </div>
          </div>

          {/* Formulaire de confirmation de paiement */}
          {showPaymentConfirm && (
            <div className="border rounded-md p-4 bg-yellow-50">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                Confirmation de paiement
              </h3>
              <div className="mb-3">
                <label
                  htmlFor="transactionId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Référence de la transaction
                </label>
                <input
                  type="text"
                  id="transactionId"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  placeholder="Entrez la référence de votre paiement"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowPaymentConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  disabled={!transactionId.trim()}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                    transactionId.trim()
                      ? "bg-primary hover:bg-primary-dark"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Confirmer
                </button>
              </div>
            </div>
          )}

          {/* Actions et boutons */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex flex-wrap gap-2 justify-end">
              {/* Confirmer paiement */}
              {onConfirmPayment && order.payment_status === "pending" && (
                <button
                  type="button"
                  onClick={() => setShowPaymentConfirm(true)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 inline-flex items-center"
                >
                  <CreditCardIcon className="h-5 w-5 mr-1" />
                  Confirmer paiement
                </button>
              )}

              {/* Valider commande */}
              {onValidateOrder && (
                <button
                  type="button"
                  onClick={onValidateOrder}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 inline-flex items-center"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-1" />
                  Valider
                </button>
              )}

              {/* Expédier commande */}
              {onShipOrder && (
                <button
                  type="button"
                  onClick={onShipOrder}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 inline-flex items-center"
                >
                  <TruckIcon className="h-5 w-5 mr-1" />
                  Expédier
                </button>
              )}

              {/* Marquer comme livrée */}
              {onMarkDelivered && (
                <button
                  type="button"
                  onClick={onMarkDelivered}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 inline-flex items-center"
                >
                  <HomeIcon className="h-5 w-5 mr-1" />
                  Marquer comme livrée
                </button>
              )}

              {/* Rembourser */}
              {onRefundOrder && (
                <button
                  type="button"
                  onClick={onRefundOrder}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 inline-flex items-center"
                >
                  <CreditCardIcon className="h-5 w-5 mr-1" />
                  Rembourser
                </button>
              )}

              {/* Annuler commande */}
              {onCancelOrder && (
                <button
                  type="button"
                  onClick={onCancelOrder}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 inline-flex items-center"
                >
                  <XCircleIcon className="h-5 w-5 mr-1" />
                  Annuler la commande
                </button>
              )}

              {/* Fermer modal */}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
